import axios from 'axios';
import { REST_HOSTNAME } from '../Config';
import { ChineseTeacher } from './UserApi';

const playbackSettingsChangedEvent = 'custom-sentence-playback-settings-changed';
const playbackSettingsRevisionKey = 'customSentencePlaybackSettingsRevision';

const axiosInstance = axios.create({
    baseURL: REST_HOSTNAME
});

// Add request interceptor
axiosInstance.interceptors.request.use(
    function (config) {
        const jwt = localStorage.getItem('jwt');
        if (jwt) {
            config.headers['x-access-token'] = jwt; // Add JWT to the headers
        }
        return config;
    },
    function (error) {
        return Promise.reject(error);
    }
);

export const AudioApi = {

    selectedCustomSentenceTeacher: function (): ChineseTeacher {
        // The account's teacher choice is shared with lessons and student settings.
        // Keep the older pronunciation-only value as a fallback for existing clients.
        const pronunciationTeacher = localStorage.getItem('selectedCustomSentenceVoice');
        const selected = localStorage.getItem('teacher') ||
            (pronunciationTeacher !== 'null' ? pronunciationTeacher : null);
        return selected === ChineseTeacher.Female || selected === ChineseTeacher.Female2
            ? selected : ChineseTeacher.Male;
    },

    setSelectedCustomSentenceTeacher: function (teacher: ChineseTeacher): void {
        if (localStorage.getItem('teacher') === teacher) return;
        localStorage.setItem('teacher', teacher);
        AudioApi.notifyCustomSentencePlaybackSettingsChanged();
    },

    notifyCustomSentencePlaybackSettingsChanged: function (): void {
        // A storage event reaches other open tabs; the custom event refreshes this tab.
        localStorage.setItem(playbackSettingsRevisionKey, `${Date.now()}:${Math.random()}`);
        window.dispatchEvent(new Event(playbackSettingsChangedEvent));
    },

    subscribeCustomSentenceTeacher: function (listener: () => void): () => void {
        const onStorage = (event: StorageEvent) => {
            if (event.key === 'teacher' || event.key === 'selectedCustomSentenceVoice' ||
                event.key === playbackSettingsRevisionKey || event.key === null) listener();
        };
        window.addEventListener('storage', onStorage);
        window.addEventListener(playbackSettingsChangedEvent, listener);
        return () => {
            window.removeEventListener('storage', onStorage);
            window.removeEventListener(playbackSettingsChangedEvent, listener);
        };
    },

    getTeacherSentence: function (sentenceId: number, useCustomSentenceVoice = true) {
        const teacher = useCustomSentenceVoice
            ? AudioApi.selectedCustomSentenceTeacher()
            : localStorage.getItem('teacher') || ChineseTeacher.Male;
        let audioPromise;

        if (teacher == ChineseTeacher.Male) {
            audioPromise = AudioApi.getMale(sentenceId)
        } else if (teacher == ChineseTeacher.Female) {
            audioPromise = AudioApi.getFemale(sentenceId)
        } else if (teacher == ChineseTeacher.Female2) {
            audioPromise = AudioApi.getFemale2(sentenceId)
        } else {
            throw Error("unknown selected teacher name in local storage " + teacher);
        }
        return audioPromise
    },

    getMale: function (sentenceId: number) {
        return axiosInstance.request<Blob>({
            method: "GET",
            url: `/audio/male/${sentenceId}`,
            responseType: "blob"
        });
    },
    getFemale: function (sentenceId: number) {
        return axiosInstance.request<Blob>({
            method: "GET",
            url: `/audio/female/${sentenceId}`,
            responseType: "blob"
        });
    },
    getFemale2: function (sentenceId: number) {
        return axiosInstance.request<Blob>({
            method: "GET",
            url: `/audio/female2/${sentenceId}`,
            responseType: "blob"
        });
    },
    getSpeakerA: function (sentenceId: number) {
        return axiosInstance.request<Blob>({
            method: "GET",
            url: `/audio/speaker/${sentenceId}?name=A`,
            responseType: "blob"
        });
    },
    getSpeakerB: function (sentenceId: number) {
        return axiosInstance.request<Blob>({
            method: "GET",
            url: `/audio/speaker/${sentenceId}?name=B`,
            responseType: "blob"
        });
    },
    getPublicSentenceScoreAudio: function (publicSentenceScoreId: string) {
        return axiosInstance.request<Blob>({
            method: "GET",
            url: `/public_sentence_score_audio/${publicSentenceScoreId}`,
            responseType: "blob"
        });
    },
    getSentenceScoreAudio: function (sentenceScoreId: number) {
        return axiosInstance.request<Blob>({
            method: "GET",
            url: `/sentence_score_audio/${sentenceScoreId}`,
            responseType: "blob"
        });
    },
    getVerbaScholaStudentFeedbackRecording: function (sentenceScoreId: number) {
        return axiosInstance.request<Blob>({
            method: "GET",
            url: `/student/verbaschola/feedback/recordings/${sentenceScoreId}`,
            responseType: "blob"
        });
    },
    getMaleByText: function (text: string) {
        return axiosInstance.request<Blob>({
            method: "PUT",
            url: `/audio/male/text`,
            data: { text },
            responseType: "blob"
        });
    },
    getFemaleByText: function (text: string) {
        return axiosInstance.request<Blob>({
            method: "PUT",
            url: `/audio/female/text`,
            data: { text },
            responseType: "blob"
        });
    },
    getFemale2ByText: function (text: string) {
        return axiosInstance.request<Blob>({
            method: "PUT",
            url: `/audio/female2/text`,
            data: { text },
            responseType: "blob"
        });
    },
    getSpeakerAByText: function (text: string) {
        return axiosInstance.request<Blob>({
            method: "PUT",
            url: `/audio/speaker/A/text`,
            data: { text },
            responseType: "blob"
        });
    },
    getSpeakerBByText: function (text: string) {
        return axiosInstance.request<Blob>({
            method: "PUT",
            url: `/audio/speaker/B/text`,
            data: { text },
            responseType: "blob"
        });
    },
}
